import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';

interface Backup {
  key: string;
  type: string;
  tenantId: string | null;
  createdAt: string;
}

export default function Backups() {
  const [backupType, setBackupType] = useState<'all' | 'system' | 'tenant'>('all');
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['backups', backupType],
    queryFn: () =>
      api.get<{ backups: Backup[] }>('/admin/backups/list', { type: backupType }),
  });

  const createSystemBackupMutation = useMutation({
    mutationFn: () => api.post('/admin/backups/system'),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['backups'] }),
  });

  const cleanupMutation = useMutation({
    mutationFn: (daysOld: number) =>
      api.delete(`/admin/backups/cleanup?daysOld=${daysOld}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['backups'] }),
  });

  const backups = data?.data.backups || [];

  const handleDownload = (backupKey: string) => {
    window.open(`/api/admin/backups/download/${encodeURIComponent(backupKey)}`, '_blank');
  };

  return (
    <div className="page">
      <div className="page-header">
        <h1>Backup Management</h1>
        <div className="header-actions">
          <button
            onClick={() => createSystemBackupMutation.mutate()}
            className="btn btn-primary"
            disabled={createSystemBackupMutation.isPending}
          >
            {createSystemBackupMutation.isPending
              ? 'Creating...'
              : 'Create System Backup'}
          </button>
        </div>
      </div>

      <div className="card">
        <h3>Backup Actions</h3>
        <div className="action-buttons">
          <button
            onClick={() => {
              const days = prompt('Delete backups older than how many days?', '90');
              if (days && !isNaN(parseInt(days))) {
                cleanupMutation.mutate(parseInt(days));
              }
            }}
            className="btn btn-secondary"
            disabled={cleanupMutation.isPending}
          >
            {cleanupMutation.isPending ? 'Cleaning...' : 'Cleanup Old Backups'}
          </button>
        </div>
      </div>

      <div className="filters">
        <div className="filter-tabs">
          <button
            className={`tab ${backupType === 'all' ? 'active' : ''}`}
            onClick={() => setBackupType('all')}
          >
            All Backups
          </button>
          <button
            className={`tab ${backupType === 'system' ? 'active' : ''}`}
            onClick={() => setBackupType('system')}
          >
            System Backups
          </button>
          <button
            className={`tab ${backupType === 'tenant' ? 'active' : ''}`}
            onClick={() => setBackupType('tenant')}
          >
            Tenant Backups
          </button>
        </div>
      </div>

      {isLoading ? (
        <div className="loading">Loading backups...</div>
      ) : backups.length === 0 ? (
        <div className="empty-state">
          <p>No backups found</p>
          <button
            onClick={() => createSystemBackupMutation.mutate()}
            className="btn btn-primary"
            disabled={createSystemBackupMutation.isPending}
          >
            Create First Backup
          </button>
        </div>
      ) : (
        <div className="table-container">
          <table className="table">
            <thead>
              <tr>
                <th>Backup Path</th>
                <th>Type</th>
                <th>Tenant</th>
                <th>Created</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {backups.map((backup) => (
                <tr key={backup.key}>
                  <td>
                    <code className="truncate" title={backup.key}>
                      {backup.key.split('/').pop()}
                    </code>
                  </td>
                  <td>
                    <span className="badge">{backup.type}</span>
                  </td>
                  <td>{backup.tenantId || '-'}</td>
                  <td>{new Date(backup.createdAt).toLocaleString()}</td>
                  <td>
                    <button
                      onClick={() => handleDownload(backup.key)}
                      className="btn btn-small"
                    >
                      Download
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {createSystemBackupMutation.isSuccess && (
        <div className="success-message">
          System backup created successfully!
        </div>
      )}

      {cleanupMutation.isSuccess && (
        <div className="success-message">
          Old backups cleaned up successfully!
        </div>
      )}
    </div>
  );
}
